import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, MapPin } from "lucide-react";
import { NAV_LINKS, SITE, STORES } from "@/data/site";
import { useSite } from "@/context/SiteContext";
import { api, assetUrl } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const { settings } = useSite();
  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to the Adam's Family");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email");
    }
  };

  return (
    <footer className="bg-espresso text-ivory/80 font-sans" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            {settings.logo_url ? (
              <img src={assetUrl(settings.logo_url)} alt={settings.brand_name} className="h-16 w-auto object-contain" />
            ) : (
              <>
                <span className="font-serif text-3xl font-semibold text-ivory">{settings.brand_name}</span>
                <p className="font-accent text-[10px] tracking-[0.3em] text-champagne uppercase mt-1">{settings.brand_tagline}</p>
              </>
            )}
            <p className="text-sm mt-5 leading-relaxed text-ivory/60 max-w-xs">
              A destination for elegant Islamic modest fashion — designed with grace, crafted with care.
            </p>
            <div className="flex gap-4 mt-6">
              <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="hover:text-champagne transition-colors" data-testid="footer-instagram"><Instagram className="w-5 h-5" /></a>
            </div>
          </div>

          <div>
            <h4 className="overline mb-5">Shop</h4>
            <ul className="space-y-3 text-sm">
              {NAV_LINKS.slice(1, 7).map((l) => (
                <li key={l.path}><Link to={l.path} className="hover:text-champagne transition-colors">{l.name}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="overline mb-5">Our Stores</h4>
            <ul className="space-y-3 text-sm">
              {STORES.map((s) => (
                <li key={s.name}>
                  <a href={s.maps} target="_blank" rel="noreferrer" className="flex items-start gap-2 hover:text-champagne transition-colors">
                    <MapPin className="w-4 h-4 mt-0.5 text-champagne shrink-0" />
                    <span>{s.name}, {s.city}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="overline mb-5">Join the Adam's Family</h4>
            <p className="text-sm text-ivory/60 mb-4">Be the first to discover new arrivals & exclusive offers.</p>
            <form onSubmit={subscribe} className="flex" data-testid="footer-newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="flex-1 bg-transparent border border-ivory/25 rounded-l-full px-4 py-2.5 text-sm outline-none focus:border-champagne text-ivory placeholder:text-ivory/40"
                data-testid="footer-newsletter-input"
              />
              <button type="submit" className="bg-champagne text-espresso px-5 rounded-r-full text-sm font-medium hover:bg-champagne-light transition-colors" data-testid="footer-newsletter-submit">Join</button>
            </form>
          </div>
        </div>

        <div className="border-t border-ivory/10 mt-14 pt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-ivory/50">
          <p>© {new Date().getFullYear()} Adam's The Islamic Boutique. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/about" className="hover:text-champagne">About Us</Link>
            <Link to="/contact" className="hover:text-champagne">Contact</Link>
            <Link to="/exchange-policy" className="hover:text-champagne" data-testid="footer-exchange-policy">Exchange Policy</Link>
            <Link to="/terms" className="hover:text-champagne" data-testid="footer-terms">Terms &amp; Conditions</Link>
            <Link to="/privacy" className="hover:text-champagne" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/cancellation" className="hover:text-champagne" data-testid="footer-cancellation">Cancellation Policy</Link>
            <a href={`https://wa.me/${settings.whatsapp}`} target="_blank" rel="noreferrer" className="hover:text-champagne">WhatsApp</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
